import type { ActivityWithTracks, MapWithRoutes } from '../types/Geo';
import { getGroupedSegments, processGroupedSegments } from '../utils/legacyGeoJSONConversions';

type MapWithIdentifiers = { type?: string; trailId?: number };

/**
 * Gets elevation data for a map with distances calculated between points.
 *
 * @param map - The map object (trail, route, or activity)
 * @param experimentFlagEnabled - Whether the 3D distance experiment flag is enabled.
 *                                 If true AND the map is a verified trail, uses 3D distance.
 *                                 Otherwise, uses 2D distance.
 * @returns Array of elevation point segments
 */
const getMapElevationData = (map: Partial<ActivityWithTracks> | Partial<MapWithRoutes>, experimentFlagEnabled?: boolean) => {
  const groupedSegments = getGroupedSegments(map);
  const segments = groupedSegments?.map(group => group.segments).flat();

  if (!segments || segments.length < 1) {
    return [];
  }

  // Determine whether to use 3D distance
  // BOTH conditions must be true:
  // 1. Experiment flag must be enabled (experimentFlagEnabled === true)
  // 2. Map must be a verified trail (type === 'AtVerifiedMap' OR has trailId)
  const mapWithIdentifiers = map as MapWithIdentifiers;
  const isVerifiedTrail = mapWithIdentifiers.type === 'AtVerifiedMap' || !!mapWithIdentifiers.trailId;
  const use3DDistance = experimentFlagEnabled === true && isVerifiedTrail;

  const { pointByPointSegmented } = processGroupedSegments(groupedSegments, true, use3DDistance);
  return pointByPointSegmented;
};

export default getMapElevationData;
